import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import {
  Plus,
  School,
  Users,
  BookOpen,
  Edit,
  Trash2,
  X,
  UserPlus,
  BookPlus,
  Search,
  Loader2,
  ChevronLeft,
  GraduationCap,
  RefreshCw
} from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../../api';
import { useAuth } from '../../contexts/AuthContext';

interface StudentSummary {
  _id: string;
  name: string;
  email: string;
  avatar?: string;
}

interface CourseSummary {
  _id: string;
  title: string;
  thumbnail?: string;
  level: string;
  category?: { _id: string; name: string } | null;
  isPublished?: boolean;
}

interface ProfessorSummary {
  _id: string;
  name: string;
  email: string;
  avatar?: string;
}

interface ClassData {
  _id: string;
  name: string;
  level: string;
  description: string;
  tuition?: number;
  professors: ProfessorSummary[];
  professorCount: number;
  students: StudentSummary[];
  studentCount: number;
  courses: CourseSummary[];
  courseCount: number;
  isActive: boolean;
  createdAt: string;
}

const LEVEL_SUGGESTIONS = [
  'BTS 1ère année',
  'BTS 2ème année',
  'U17 Régional',
  'U19 Élite',
  'Séniors 1',
  'BTS CG 1A',
  'BTS CG 2A',
  'BTS MCO 1A',
  'BTS MCO 2A'
];

const Classes: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [classes, setClasses] = useState<ClassData[]>([]);
  const [loading, setLoading] = useState(true);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingClass, setEditingClass] = useState<ClassData | null>(null);
  const [formData, setFormData] = useState({ name: '', level: '', description: '', tuition: '' });
  const [saving, setSaving] = useState(false);

  // Detail state
  const [selectedClass, setSelectedClass] = useState<ClassData | null>(null);

  // Student picker
  const [showStudentPicker, setShowStudentPicker] = useState(false);
  const [availableStudents, setAvailableStudents] = useState<StudentSummary[]>([]);
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [searchStudents, setSearchStudents] = useState('');

  // Course picker
  const [showCoursePicker, setShowCoursePicker] = useState(false);
  const [availableCourses, setAvailableCourses] = useState<CourseSummary[]>([]);
  const [selectedCourses, setSelectedCourses] = useState<string[]>([]);
  const [searchCourses, setSearchCourses] = useState('');

  // Professor picker
  const [showProfessorPicker, setShowProfessorPicker] = useState(false);
  const [availableProfessors, setAvailableProfessors] = useState<ProfessorSummary[]>([]);
  const [selectedProfessors, setSelectedProfessors] = useState<string[]>([]);
  const [searchProfessors, setSearchProfessors] = useState('');

  const [pickingLoading, setPickingLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const loadClasses = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/classes');
      setClasses(response.data.data || []);
    } catch (error) {
      console.error('Load classes error:', error);
      toast.error('Erreur lors du chargement des classes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  const openCreateForm = () => {
    setEditingClass(null);
    setFormData({ name: '', level: '', description: '', tuition: '' });
    setShowForm(true);
  };

  const openEditForm = (cls: ClassData) => {
    setEditingClass(cls);
    setFormData({
      name: cls.name,
      level: cls.level,
      description: cls.description,
      tuition: cls.tuition !== undefined ? String(cls.tuition) : ''
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingClass(null);
    setFormData({ name: '', level: '', description: '', tuition: '' });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.level.trim()) {
      toast.error('Le nom et le niveau sont obligatoires');
      return;
    }

    setSaving(true);
    try {
      const url = editingClass ? `/classes/${editingClass._id}` : '/classes';
      const method = editingClass ? 'put' : 'post';

      const payload = {
        name: formData.name,
        level: formData.level,
        description: formData.description,
        tuition: formData.tuition !== '' ? parseFloat(formData.tuition) : 0
      };

      const response = await api[method](url, payload);
      toast.success(editingClass ? 'Classe mise à jour' : 'Classe créée');
      await loadClasses();
      closeForm();

      if (selectedClass && editingClass?._id === selectedClass._id) {
        setSelectedClass(response.data.data);
      }
    } catch (error: any) {
      console.error('Save class error:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cls: ClassData) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir désactiver la classe « ${cls.name} » ?`)) return;

    try {
      await api.delete(`/classes/${cls._id}`);
      toast.success('Classe désactivée');
      if (selectedClass?._id === cls._id) setSelectedClass(null);
      loadClasses();
    } catch (error: any) {
      console.error('Delete class error:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la désactivation');
    }
  };

  const openDetail = (cls: ClassData) => {
    setSelectedClass(cls);
    setShowStudentPicker(false);
    setShowCoursePicker(false);
    setShowProfessorPicker(false);
  };

  const refreshDetail = async (classId: string) => {
    try {
      const response = await api.get(`/classes/${classId}`);
      setSelectedClass(response.data.data);
      const listIndex = classes.findIndex((c) => c._id === classId);
      if (listIndex !== -1) {
        const updated = [...classes];
        updated[listIndex] = response.data.data;
        setClasses(updated);
      }
    } catch (error) {
      console.error('Refresh class detail error:', error);
    }
  };

  // ==== Professor picker ====
  const openProfessorPicker = async () => {
    if (!selectedClass) return;
    setPickingLoading(true);
    setSelectedProfessors([]);
    setSearchProfessors('');
    setShowProfessorPicker(true);
    try {
      const response = await api.get('/classes/available-professors');
      setAvailableProfessors(response.data.data || []);
    } catch (error) {
      console.error('Load available professors error:', error);
      toast.error('Erreur lors du chargement des professeurs');
    } finally {
      setPickingLoading(false);
    }
  };

  const toggleProfessor = (id: string) => {
    setSelectedProfessors((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const assignProfessors = async () => {
    if (!selectedClass || selectedProfessors.length === 0) return;
    setAssigning(true);
    try {
      const response = await api.post(`/classes/${selectedClass._id}/professors`, {
        professorIds: selectedProfessors
      });
      toast.success(response.data.message);
      setShowProfessorPicker(false);
      await refreshDetail(selectedClass._id);
    } catch (error: any) {
      console.error('Assign professors error:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de l\'affectation');
    } finally {
      setAssigning(false);
    }
  };

  const removeProfessor = async (professorId: string) => {
    if (!selectedClass) return;
    if (!window.confirm('Retirer ce professeur de la classe ?')) return;

    try {
      await api.delete(`/classes/${selectedClass._id}/professors/${professorId}`);
      toast.success('Professeur retiré de la classe');
      await refreshDetail(selectedClass._id);
    } catch (error: any) {
      console.error('Remove professor error:', error);
      toast.error(error.response?.data?.message || 'Erreur lors du retrait');
    }
  };

  // ==== Student picker ====
  const openStudentPicker = async () => {
    if (!selectedClass) return;
    setPickingLoading(true);
    setSelectedStudents([]);
    setSearchStudents('');
    setShowStudentPicker(true);
    try {
      const response = await api.get(`/classes/${selectedClass._id}/available-students`);
      setAvailableStudents(response.data.data || []);
    } catch (error) {
      console.error('Load available students error:', error);
      toast.error('Erreur lors du chargement des étudiants');
    } finally {
      setPickingLoading(false);
    }
  };

  const toggleStudent = (id: string) => {
    setSelectedStudents((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const assignStudents = async () => {
    if (!selectedClass || selectedStudents.length === 0) return;
    setAssigning(true);
    try {
      const response = await api.post(`/classes/${selectedClass._id}/students`, {
        studentIds: selectedStudents
      });
      toast.success(response.data.message);
      setShowStudentPicker(false);
      await refreshDetail(selectedClass._id);
    } catch (error: any) {
      console.error('Assign students error:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de l\'affectation');
    } finally {
      setAssigning(false);
    }
  };

  const removeStudent = async (studentId: string) => {
    if (!selectedClass) return;
    if (!window.confirm('Retirer cet étudiant de la classe ?')) return;

    try {
      await api.delete(`/classes/${selectedClass._id}/students/${studentId}`);
      toast.success('Étudiant retiré de la classe');
      await refreshDetail(selectedClass._id);
    } catch (error: any) {
      console.error('Remove student error:', error);
      toast.error(error.response?.data?.message || 'Erreur lors du retrait');
    }
  };

  // ==== Course picker ====
  const openCoursePicker = async () => {
    if (!selectedClass) return;
    setPickingLoading(true);
    setSelectedCourses([]);
    setSearchCourses('');
    setShowCoursePicker(true);
    try {
      const response = await api.get(`/classes/${selectedClass._id}/available-courses`);
      setAvailableCourses(response.data.data || []);
    } catch (error) {
      console.error('Load available courses error:', error);
      toast.error('Erreur lors du chargement des cours');
    } finally {
      setPickingLoading(false);
    }
  };

  const toggleCourse = (id: string) => {
    setSelectedCourses((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const assignCourses = async () => {
    if (!selectedClass || selectedCourses.length === 0) return;
    setAssigning(true);
    try {
      const response = await api.post(`/classes/${selectedClass._id}/courses`, {
        courseIds: selectedCourses
      });
      toast.success(response.data.message);
      setShowCoursePicker(false);
      await refreshDetail(selectedClass._id);
    } catch (error: any) {
      console.error('Assign courses error:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de l\'affectation');
    } finally {
      setAssigning(false);
    }
  };

  const removeCourse = async (courseId: string) => {
    if (!selectedClass) return;
    if (!window.confirm('Retirer ce cours de la classe ?')) return;

    try {
      await api.delete(`/classes/${selectedClass._id}/courses/${courseId}`);
      toast.success('Cours retiré de la classe');
      await refreshDetail(selectedClass._id);
    } catch (error: any) {
      console.error('Remove course error:', error);
      toast.error(error.response?.data?.message || 'Erreur lors du retrait');
    }
  };

  const filteredAvailableStudents = availableStudents.filter((s) => {
    const q = searchStudents.toLowerCase();
    return s.name.toLowerCase().includes(q) || s.email.toLowerCase().includes(q);
  });

  const filteredAvailableCourses = availableCourses.filter((c) =>
    c.title.toLowerCase().includes(searchCourses.toLowerCase())
  );

  const filteredAvailableProfessors = availableProfessors.filter((p) => {
    const q = searchProfessors.toLowerCase();
    const matchesSearch = !q || p.name.toLowerCase().includes(q) || p.email.toLowerCase().includes(q);
    const alreadyAssigned = selectedClass?.professors.some((cp) => cp._id === p._id);
    return matchesSearch && !alreadyAssigned;
  });

  const currentProfessorsNotInAvailable = selectedClass?.professors.filter(
    (cp) => !availableProfessors.some((ap) => ap._id === cp._id)
  ) || [];

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Gestion des Classes</h1>
          {currentUser?.role === 'admin' && (
            <button
              onClick={openCreateForm}
              className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nouvelle classe
            </button>
          )}
        </div>

        {/* Create / Edit Form */}
        {showForm && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingClass ? 'Modifier la classe' : 'Nouvelle classe'}
              </h2>
              <button onClick={closeForm} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom de la classe *
                </label>
                <input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  placeholder="Ex: U19 Élite"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Niveau *
                </label>
                <input
                  list="level-suggestions"
                  value={formData.level}
                  onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  placeholder="Ex: BTS 1ère année"
                />
                <datalist id="level-suggestions">
                  {LEVEL_SUGGESTIONS.map((level) => (
                    <option key={level} value={level} />
                  ))}
                </datalist>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  placeholder="Description de la classe..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Frais mensuels (€/mois)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.tuition}
                  onChange={(e) => setFormData({ ...formData, tuition: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  placeholder="Ex: 5000"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50"
                >
                  {saving ? <Loader2 className="animate-spin h-5 w-5" /> : editingClass ? 'Enregistrer' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Classes List */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="animate-spin h-10 w-10 text-gray-400" />
          </div>
        ) : classes.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 text-center py-16">
            <div className="text-gray-400 mb-4">
              <School className="h-16 w-16 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune classe trouvée</h3>
            <p className="text-gray-500">
              Commencez par créer votre première classe
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {classes.map((cls) => (
              <div
                key={cls._id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-brand-100 text-brand-600 rounded-lg flex items-center justify-center mr-3">
                      <School className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{cls.name}</h3>
                      <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-brand-50 text-brand-700 rounded-full">
                        {cls.level}
                      </span>
                    </div>
                  </div>
                  <div className="flex space-x-1">
                    {currentUser?.role === 'admin' && (
                      <>
                        <button
                          onClick={() => openEditForm(cls)}
                          className="p-1.5 text-gray-400 hover:text-brand-600 transition-colors"
                          title="Modifier"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(cls)}
                          className="p-1.5 text-gray-400 hover:text-brand-600 transition-colors"
                          title="Désactiver"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {cls.description && (
                  <p className="mt-3 text-sm text-gray-500 line-clamp-2">{cls.description}</p>
                )}

                {cls.professors.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {cls.professors.map((prof) => (
                      <span key={prof._id} className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-purple-50 text-purple-700 rounded-full">
                        <GraduationCap className="h-3 w-3 mr-1" />
                        {prof.name}
                      </span>
                    ))}
                  </div>
                )}

                <div className="mt-4 flex items-center space-x-6 text-sm text-gray-600">
                  <span className="flex items-center">
                    <Users className="h-4 w-4 mr-1 text-gray-400" />
                    {cls.studentCount} étudiant(s)
                  </span>
                  <span className="flex items-center">
                    <BookOpen className="h-4 w-4 mr-1 text-gray-400" />
                    {cls.courseCount} cours
                  </span>
                </div>

                <button
                  onClick={() => openDetail(cls)}
                  className="mt-4 w-full px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
                >
                  Gérer la classe
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Detail Panel */}
        {selectedClass && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center">
                <button
                  onClick={() => setSelectedClass(null)}
                  className="mr-3 text-gray-400 hover:text-gray-600"
                  title="Retour"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{selectedClass.name}</h2>
                  <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-brand-50 text-brand-700 rounded-full">
                    {selectedClass.level}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelectedClass(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
              {/* Professors */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900 flex items-center">
                    <GraduationCap className="h-4 w-4 mr-2 text-brand-600" />
                    Professeurs ({selectedClass.professorCount})
                  </h3>
                  {currentUser?.role === 'admin' && (
                    <button
                      onClick={openProfessorPicker}
                      className="flex items-center text-sm text-brand-600 hover:text-brand-800"
                    >
                      <UserPlus className="h-4 w-4 mr-1" />
                      Ajouter
                    </button>
                  )}
                </div>

                {selectedClass.professors.length === 0 ? (
                  <div className="text-center py-10 text-gray-500">
                    <GraduationCap className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                    Aucun professeur dans cette classe
                  </div>
                ) : (
                  <ul className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
                    {selectedClass.professors.map((prof) => (
                      <li key={prof._id} className="px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center min-w-0">
                          <div className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center text-xs font-medium mr-3 flex-shrink-0">
                            {prof.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{prof.name}</p>
                            <p className="text-xs text-gray-500 truncate">{prof.email}</p>
                          </div>
                        </div>
                        {currentUser?.role === 'admin' && (
                          <button
                            onClick={() => removeProfessor(prof._id)}
                            className="ml-2 text-gray-400 hover:text-brand-600 transition-colors"
                            title="Retirer"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Students */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900 flex items-center">
                    <Users className="h-4 w-4 mr-2 text-brand-600" />
                    Étudiants ({selectedClass.studentCount})
                  </h3>
                  {currentUser?.role === 'admin' && (
                    <button
                      onClick={openStudentPicker}
                      className="flex items-center text-sm text-brand-600 hover:text-brand-800"
                    >
                      <UserPlus className="h-4 w-4 mr-1" />
                      Ajouter
                    </button>
                  )}
                </div>

                {selectedClass.students.length === 0 ? (
                  <div className="text-center py-10 text-gray-500">
                    <GraduationCap className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                    Aucun étudiant dans cette classe
                  </div>
                ) : (
                  <ul className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
                    {selectedClass.students.map((student) => (
                      <li key={student._id} className="px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center min-w-0">
                          <div className="w-8 h-8 rounded-full bg-brand-600 text-white flex items-center justify-center text-xs font-medium mr-3 flex-shrink-0">
                            {student.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{student.name}</p>
                            <p className="text-xs text-gray-500 truncate">{student.email}</p>
                          </div>
                        </div>
                        {currentUser?.role === 'admin' && (
                          <button
                            onClick={() => removeStudent(student._id)}
                            className="ml-2 text-gray-400 hover:text-brand-600 transition-colors"
                            title="Retirer"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Courses */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900 flex items-center">
                    <BookOpen className="h-4 w-4 mr-2 text-brand-600" />
                    Cours ({selectedClass.courseCount})
                  </h3>
                  <button
                    onClick={openCoursePicker}
                    className="flex items-center text-sm text-brand-600 hover:text-brand-800"
                  >
                    <BookPlus className="h-4 w-4 mr-1" />
                    Ajouter
                  </button>
                </div>

                {selectedClass.courses.length === 0 ? (
                  <div className="text-center py-10 text-gray-500">
                    <BookOpen className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                    Aucun cours affecté à cette classe
                  </div>
                ) : (
                  <ul className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
                    {selectedClass.courses.map((course) => (
                      <li key={course._id} className="px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center min-w-0">
                          {course.thumbnail ? (
                            <img
                              src={course.thumbnail}
                              alt={course.title}
                              className="w-10 h-10 rounded object-cover mr-3 flex-shrink-0"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-brand-100 text-brand-600 rounded flex items-center justify-center mr-3 flex-shrink-0">
                              <BookOpen className="h-4 w-4" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{course.title}</p>
                            <p className="text-xs text-gray-500 truncate">
                              {course.category?.name || 'Sans catégorie'}
                              {course.isPublished ? ' • Publié' : ''}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => removeCourse(course._id)}
                          className="ml-2 text-gray-400 hover:text-brand-600 transition-colors"
                          title="Retirer"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Student Picker Modal */}
        {showStudentPicker && selectedClass && (
          <Modal title={`Ajouter des étudiants à ${selectedClass.name}`} onClose={() => setShowStudentPicker(false)}>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un étudiant..."
                value={searchStudents}
                onChange={(e) => setSearchStudents(e.target.value)}
                className="pl-9 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>

            <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
              {pickingLoading ? (
                <div className="p-8 text-center">
                  <Loader2 className="animate-spin h-8 w-8 mx-auto text-gray-400" />
                </div>
              ) : filteredAvailableStudents.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  {searchStudents
                    ? 'Aucun étudiant ne correspond à la recherche'
                    : 'Tous les étudiants sont déjà affectés à cette classe'}
                </div>
              ) : (
                filteredAvailableStudents.map((student) => (
                  <label
                    key={student._id}
                    className="flex items-center px-4 py-3 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedStudents.includes(student._id)}
                      onChange={() => toggleStudent(student._id)}
                      className="rounded border-gray-300 text-brand-600 focus:ring-brand-500 mr-3"
                    />
                    <div className="w-8 h-8 rounded-full bg-brand-600 text-white flex items-center justify-center text-xs font-medium mr-3">
                      {student.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{student.name}</p>
                      <p className="text-xs text-gray-500 truncate">{student.email}</p>
                    </div>
                  </label>
                ))
              )}
            </div>

            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-gray-600">{selectedStudents.length} sélectionné(s)</p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowStudentPicker(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={assignStudents}
                  disabled={assigning || selectedStudents.length === 0}
                  className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50"
                >
                  {assigning ? <Loader2 className="animate-spin h-5 w-5" /> : 'Affecter'}
                </button>
              </div>
            </div>
          </Modal>
        )}

        {/* Course Picker Modal */}
        {showCoursePicker && selectedClass && (
          <Modal title={`Ajouter des cours à ${selectedClass.name}`} onClose={() => setShowCoursePicker(false)}>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un cours..."
                value={searchCourses}
                onChange={(e) => setSearchCourses(e.target.value)}
                className="pl-9 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>

            <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
              {pickingLoading ? (
                <div className="p-8 text-center">
                  <Loader2 className="animate-spin h-8 w-8 mx-auto text-gray-400" />
                </div>
              ) : filteredAvailableCourses.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  {searchCourses
                    ? 'Aucun cours ne correspond à la recherche'
                    : currentUser?.role === 'professor'
                      ? 'Aucun de vos cours disponible à affecter'
                      : 'Aucun cours disponible à affecter'}
                </div>
              ) : (
                filteredAvailableCourses.map((course) => (
                  <label
                    key={course._id}
                    className="flex items-center px-4 py-3 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedCourses.includes(course._id)}
                      onChange={() => toggleCourse(course._id)}
                      className="rounded border-gray-300 text-brand-600 focus:ring-brand-500 mr-3"
                    />
                    <div className="w-9 h-9 rounded bg-brand-100 text-brand-600 flex items-center justify-center mr-3 flex-shrink-0">
                      <BookOpen className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{course.title}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {course.category?.name || 'Sans catégorie'}
                        {course.isPublished !== false ? ' • Publié' : ' • Non publié'}
                      </p>
                    </div>
                    {course.isPublished === false && (
                      <span className="ml-auto text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full">
                        Non publié
                      </span>
                    )}
                  </label>
                ))
              )}
            </div>

            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-gray-600">{selectedCourses.length} sélectionné(s)</p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowCoursePicker(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={assignCourses}
                  disabled={assigning || selectedCourses.length === 0}
                  className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50"
                >
                  {assigning ? <Loader2 className="animate-spin h-5 w-5" /> : 'Affecter'}
                </button>
              </div>
            </div>
          </Modal>
        )}

        {/* Professor Picker Modal */}
        {showProfessorPicker && selectedClass && (
          <Modal title={`Ajouter des professeurs à ${selectedClass.name}`} onClose={() => setShowProfessorPicker(false)}>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un professeur..."
                value={searchProfessors}
                onChange={(e) => setSearchProfessors(e.target.value)}
                className="pl-9 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>

            <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
              {pickingLoading ? (
                <div className="p-8 text-center">
                  <Loader2 className="animate-spin h-8 w-8 mx-auto text-gray-400" />
                </div>
              ) : filteredAvailableProfessors.length === 0 && currentProfessorsNotInAvailable.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  {searchProfessors
                    ? 'Aucun professeur ne correspond à la recherche'
                    : 'Tous les professeurs sont déjà affectés à cette classe'}
                </div>
              ) : (
                <>
                  {filteredAvailableProfessors.map((prof) => (
                    <label
                      key={prof._id}
                      className="flex items-center px-4 py-3 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedProfessors.includes(prof._id)}
                        onChange={() => toggleProfessor(prof._id)}
                        className="rounded border-gray-300 text-brand-600 focus:ring-brand-500 mr-3"
                      />
                      <div className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center text-xs font-medium mr-3">
                        {prof.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{prof.name}</p>
                        <p className="text-xs text-gray-500 truncate">{prof.email}</p>
                      </div>
                    </label>
                  ))}
                  {currentProfessorsNotInAvailable.map((prof) => (
                    <div
                      key={prof._id}
                      className="flex items-center px-4 py-3 bg-gray-50 opacity-60"
                    >
                      <div className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center text-xs font-medium mr-3">
                        {prof.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{prof.name}</p>
                        <p className="text-xs text-gray-500 truncate">Déjà assigné</p>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>

            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-gray-600">{selectedProfessors.length} sélectionné(s)</p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowProfessorPicker(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={assignProfessors}
                  disabled={assigning || selectedProfessors.length === 0}
                  className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50"
                >
                  {assigning ? <Loader2 className="animate-spin h-5 w-5" /> : 'Affecter'}
                </button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </Layout>
  );
};

const Modal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({
  title,
  onClose,
  children
}) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
    <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
      <div className="p-6 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="p-6">{children}</div>
    </div>
  </div>
);

export default Classes;
